import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { type PropsValue } from 'react-select';

import { type OptionType } from '@hivebrite/design-system/Inputs';

import { convertToOptions } from '../utils/convertToOptions';
import { isOptionType, isOptionTypeArray } from '../utils/isOptionType';
import {
  resolveOptionFromId,
  resolveOptionsFromIds,
} from '../utils/resolveOptionsFromIds';

interface QueryFnParams {
  ids?: Array<number>;
  searchText?: string;
}

interface UseReactSelectValueAsOptionsProps<
  TQueryFnData,
  TData extends object,
  TOptionType extends OptionType,
> {
  dataToOptions: (data: Array<TData>) => Array<TOptionType>;
  defaultOptions?: Array<TOptionType>;
  enabled?: boolean;
  extractData: (response: TQueryFnData) => Array<TData>;
  isMulti?: boolean;
  queryFn: (
    params: QueryFnParams,
    signal: AbortSignal,
  ) => Promise<TQueryFnData>;
  queryKey: Array<unknown>;
  staleTime?: number;
  value?:
    | Array<number>
    | Array<string>
    | Array<TData>
    | PropsValue<TOptionType>
    | TData
    | number
    | string
    | null;
}

/**
 * Generic hook that converts IDs to option objects for ReactSelectAdaptive components.
 * If the value provided is already an option object, it will be returned as is.
 * If the value is an id or an array of ids, the data will be fetched and converted to option objects.
 * @param queryKey - Query key for react-query
 * @param value - Value to fetch the options for
 * @param enabled - Whether the query should be enabled
 * @param queryFn - Function to query the data
 * @param extractData - Function to extract data from the query response
 * @param dataToOptions - Function to convert the fetched data to option objects
 * @param staleTime - Stale time for react-query
 * @returns {Object} - Object containing the options, the loading state, and the function to add options to the cache
 */
export const useReactSelectValueAsOptions = <
  TQueryFnData,
  TData extends object,
  TOptionType extends OptionType,
>({
  queryKey,
  value,
  defaultOptions = [],
  enabled = true,
  queryFn,
  extractData,
  dataToOptions,
  staleTime,
  isMulti,
}: UseReactSelectValueAsOptionsProps<TQueryFnData, TData, TOptionType>) => {
  // We maintain a cache of existing options in order to easily convert ids to options
  // without having to fetch the data again.
  const [optionsCache, setOptionsCache] =
    useState<Array<TOptionType>>(defaultOptions);

  const addOptionsToCache = useCallback(
    (fetchedOptions: Array<TOptionType>) => {
      setOptionsCache((prev) => {
        const optionsToAdd = fetchedOptions.filter(
          (option) =>
            !prev.some((prevOption) => prevOption.value === option.value),
        );

        if (optionsToAdd.length > 0) {
          return [...prev, ...optionsToAdd];
        }

        return prev;
      });
    },
    [],
  );

  useEffect(() => {
    if (defaultOptions.length > 0) {
      addOptionsToCache(defaultOptions);
    }
  }, [defaultOptions, addOptionsToCache]);

  const { isLoading, data } = useQuery<PropsValue<TOptionType>>({
    queryKey: [...queryKey, value],
    enabled,
    queryFn: async () => {
      // Early return for empty values
      if (value === null || value === undefined) {
        return null;
      }

      if (Array.isArray(value) && value.length === 0) {
        return [];
      }

      // If value is already in OptionType format, return as is
      if (isOptionType(value) || isOptionTypeArray(value)) {
        return value;
      }

      const allAvailableOptions = [...defaultOptions, ...optionsCache];

      if (typeof value === 'number') {
        return await resolveOptionFromId(
          value,
          allAvailableOptions,
          queryFn,
          (response) => dataToOptions(extractData(response)),
          addOptionsToCache,
        );
      }

      if (typeof value === 'string') {
        return { value, label: value } as TOptionType;
      }

      if (
        Array.isArray(value) &&
        value.every((val) => typeof val === 'number')
      ) {
        return await resolveOptionsFromIds(
          value as Array<number>,
          allAvailableOptions,
          queryFn,
          (response) => dataToOptions(extractData(response)),
          addOptionsToCache,
        );
      }

      if (
        Array.isArray(value) &&
        value.every((val) => typeof val === 'string')
      ) {
        return value.map(
          (val) =>
            ({
              value: val,
              label: val,
            }) as TOptionType,
        );
      }

      return convertToOptions(value as Array<TData>, dataToOptions);
    },
    staleTime,
  });

  const valueAsOptions: PropsValue<TOptionType> = data ?? (isMulti ? [] : null);

  return {
    isLoading,
    valueAsOptions,
    optionsCache,
    addOptionsToCache,
  };
};
